// Voice capture helpers for the web voice call.
//
// Records via the browser's native MediaRecorder, then transcodes to base64
// 16 kHz mono 16-bit WAV before upload — the exact format the web voice endpoint
// received before (the old VAD path sent WAV via utils.encodeWAV, and the
// backend defaults its STT container hint to "wav"). The capture container is
// irrelevant since we always re-encode to WAV.

const TARGET_SAMPLE_RATE = 16_000

// Record containers, best first. Chrome/Firefox/Edge → webm/opus, Safari → mp4.
const PREFERRED_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]

// First record container this browser supports, or null for MediaRecorder default.
export function pickRecorderType() {
  if (typeof MediaRecorder === 'undefined') return null
  for (const t of PREFERRED_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(t)) return { mimeType: t }
    } catch {
      // isTypeSupported can throw on some browsers — keep trying
    }
  }
  return null
}

// Decode a recorded Blob and re-encode as base64 16 kHz mono 16-bit WAV,
// matching the format the backend STT expects from the web client.
export async function blobToWavBase64(blob) {
  const arrayBuffer = await blob.arrayBuffer()

  const AudioCtx = window.AudioContext || window.webkitAudioContext
  const decodeCtx = new AudioCtx()
  let audioBuffer
  try {
    audioBuffer = await decodeCtx.decodeAudioData(arrayBuffer)
  } finally {
    decodeCtx.close()
  }

  const monoSamples = await resampleToMono(audioBuffer, TARGET_SAMPLE_RATE)
  return arrayBufferToBase64(encodeWav(monoSamples, TARGET_SAMPLE_RATE))
}

// Downmix to mono and resample to `targetRate` via an OfflineAudioContext.
async function resampleToMono(audioBuffer, targetRate) {
  const frameCount = Math.max(1, Math.ceil(audioBuffer.duration * targetRate))
  const OfflineCtx =
    window.OfflineAudioContext || window.webkitOfflineAudioContext
  const offline = new OfflineCtx(1, frameCount, targetRate)

  const source = offline.createBufferSource()
  source.buffer = audioBuffer
  source.connect(offline.destination)
  source.start(0)

  const rendered = await offline.startRendering()
  return rendered.getChannelData(0) // Float32Array, mono, targetRate
}

// Encode Float32 PCM samples (-1..1) as a 16-bit PCM WAV ArrayBuffer.
function encodeWav(samples, sampleRate) {
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i))
    }
  }

  writeString(0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true)
  writeString(8, 'WAVE')
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM subchunk size
  view.setUint16(20, 1, true) // audio format: PCM
  view.setUint16(22, 1, true) // channels: mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true) // byte rate
  view.setUint16(32, 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeString(36, 'data')
  view.setUint32(40, samples.length * 2, true)

  let offset = 44
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(
      offset,
      clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff,
      true,
    )
    offset += 2
  }

  return buffer
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}
